import React from 'react'
import { object, arrayOf } from 'prop-types'

import API from 'utils/API'
import allValExists from 'utils/allValExists'
import getListFromDesc from 'utils/getListFromDesc'
import { branch } from 'baobab-react/higher-order'
import triggerEvent from 'utils/dataLayer'

import BaseTemplate from 'components/templates/BaseTemplate'
import DescSection from 'components/molecules/DescSection'
import PromotionBanner from 'components/organisms/PromotionBanner'
import DetailRelatedCampus from 'components/organisms/DetailRelatedCampus'
import SectionRelatedArticles from 'components/organisms/SectionRelatedArticles'
import StickyBottomMajorCareer from 'components/organisms/StickyBottomMajorCareer'

import CareerBanner from './CareerBanner'
import CareerMustKnow from './CareerMustKnow'
import CareerResponsibilities from './CareerResponsibilities'
import CareerMajors from './CareerMajors'
import CareerSkills from './CareerSkills'
import CareerStages from './CareerStages'
import CareerInspirationalFigures from './CareerInspirationalFigures'

const exceptionFields = ['bannerDesktopAlt', 'bannerMobileAlt']

const CareerDetail = ({ profession, campusRecommendations }) => {
  const {
    id,
    name,
    description,
    description_title: descriptionTitle,
    min_salary: minSalary,
    max_salary: maxSalary,
    fieldName,
    iconName,
    liked,
    certifications,
    min_education: minEducation,
    mainBanner,
    mustKnow, /* mustKnow section raw data */
    skills, /* skills section data */
    responsibilities, /* responsibilities data */
    promotion,
    careerStages,
    relatedMajors,
    inspirationalFigures,
    article_tags: articleTags,
    path,
  } = profession

  const mustKnowArr = mustKnow.description && getListFromDesc(mustKnow.description)
  const mustKnowData = {
    ...mustKnow,
    infos: mustKnowArr,
  }

  const careerStagesData = {
    ...careerStages,
    details: careerStages.detail && getListFromDesc(careerStages.detail),
  }

  const tagsQuery = articleTags.join('&tags[]=')
  const articlesSource = `${API.endPoints().articles}?tags[]=${tagsQuery}`

  const sections = [
    {
      target: '#description',
      text: 'Deskripsi',
      show: Boolean(descriptionTitle || description),
    },
    {
      target: '#must-know',
      text: 'Wajib Tahu',
      show: allValExists(mustKnowData, exceptionFields),
    },
    {
      target: '#responsibilities',
      text: 'Peran',
      show: Boolean(allValExists(responsibilities) && responsibilities.responsibilities.length > 0),
    },
    {
      target: '#skills',
      text: 'Keahlian',
      show: Boolean(allValExists(skills, exceptionFields) && skills.skills.length > 0),
    },
    {
      target: '#career-stages',
      text: 'Jenjang',
      show: allValExists(careerStagesData),
    },
    {
      target: '#related-majors',
      text: 'Jurusan',
      show: allValExists(relatedMajors),
    },
    {
      target: '#figures',
      text: 'Tokoh',
      show: inspirationalFigures.length > 0,
    },
    {
      target: '#related-campus',
      text: 'Kampus',
      show: campusRecommendations.length > 0,
    },
    {
      target: '#related-articles',
      text: 'Artikel',
      show: articleTags.length > 0,
    },
  ]

  return (
    <BaseTemplate
      sections={sections}
      stickyBottomContent={
        <StickyBottomMajorCareer
          title={name}
          iconName={iconName}
          iconBg="#2440a4"
          likeStatus={liked}
          likeTarget={id}
          kind="professions"
        />
      }
      actionContent={{
        likeStatus: liked,
        likeTarget: id,
        kind: 'professions',
        targetName: name
      }}
      useBackToTop
      useNewHeader
      useStickyBottom
    >
      <div className="career-detail-page">
        <CareerBanner
          name={name}
          iconName={iconName}
          field={fieldName}
          minSalary={minSalary}
          maxSalary={maxSalary}
          minEducation={minEducation}
          certifications={certifications}
          imgLink={mainBanner.desktop}
          likeStatus={liked}
          likeTarget={id}
        />
        {
          description && descriptionTitle &&
          <DescSection
            title={descriptionTitle}
            description={description}
            useIcon
          />
        }
        { allValExists(mustKnowData, exceptionFields) && <CareerMustKnow {...mustKnowData} /> }
        {
          allValExists(responsibilities) && responsibilities.responsibilities.length > 0 &&
          <CareerResponsibilities {...responsibilities} />
        }
        {
          allValExists(skills, exceptionFields) && skills.skills.length > 0 &&
          <CareerSkills {...skills} />
        }
        <PromotionBanner
          promotion={promotion}
          handleGTM={() => {
            triggerEvent('career_banner', {
              career_name: name,
              banner_name: promotion.bannerMobileAlt || promotion.bannerDesktopAlt,
              page_location: path,
            })
          }}
        />
        { allValExists(careerStages, ['detail']) && <CareerStages {...careerStagesData} /> }
        { allValExists(relatedMajors) && <CareerMajors {...relatedMajors} /> }
        { inspirationalFigures.length > 0 && <CareerInspirationalFigures figures={inspirationalFigures}/> }
        { campusRecommendations.length > 0 && (
          <DetailRelatedCampus
            schools={campusRecommendations}
            handleGTM={(campusName) => {
              triggerEvent('career_campus_rec', {
                career_name: name,
                campus_name: campusName,
                page_location: path,
              })
            }}
          />
        )}
        { articleTags.length > 0 && <SectionRelatedArticles sourceUrl={articlesSource}/> }
      </div>
      <style jsx>
        {`
          .career-detail-page {
            background-color: #fff;
            color: #0c2846;
          }
        `}
      </style>
    </BaseTemplate>
  )
}

CareerDetail.propTypes = {
  studyField: object,
  campusRecommendations: arrayOf(object),
}

export default branch({
  profession: ['serverProps', 'profession'],
  campusRecommendations: ['serverProps', 'campusRecommendations'],
}, CareerDetail)

